import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight, GitBranch, Loader2, X,
  CheckCircle2, Wrench, Globe, CalendarClock,
} from "lucide-react";
import { useBranding } from "@/lib/branding";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DexDeviceRow } from "@/components/DexDeviceRow";

import { CockpitDailyOpsStrip, CockpitOpenTickets, CockpitPeriodsTimeline } from "@/components/CockpitDailyOps";

// Admin Cockpit — two tabs:
//   1. Dashboard  → Day-1 + KPIs + tiles that link out to independent actions
//   2. Lifecycle  → the bot-heavy Story Hero + Six-Agents grid + Play the story CTA
// Left-nav lands on Dashboard by default. Presenters flip to Lifecycle for the
// narrative walkthrough.

export default function AdminCockpit() {
  const { brand } = useBranding();
  const [joiners, setJoiners] = useState(null);
  const [orchestration, setOrchestration] = useState(null);
  const [orchLoading, setOrchLoading] = useState(false);
  const [joinersOpen, setJoinersOpen] = useState(false);
  const [joinersFilter, setJoinersFilter] = useState("all"); // all | at_risk
  const [dexOpen, setDexOpen] = useState(false);
  const [dexFilter, setDexFilter] = useState("flagged"); // flagged | all
  const [dexCohort, setDexCohort] = useState(null);
  // Region tab drives every panel on the page (EMEA = demo-canonical numbers).
  const [region, setRegion] = useState("emea");

  useEffect(() => {
    endpoints.onboardingAdmin(region).then(setJoiners).catch(() => {});
    endpoints.lifecycleDexCohort(undefined, region).then(setDexCohort).catch(() => {});
  }, [region]);

  const runOrchestrator = async () => {
    setOrchLoading(true);
    try {
      const res = await endpoints.lifecycleOrchestrator({});
      setOrchestration(res);
      toast.success("Orchestrator completed — see the recommendation below.");
    } catch (e) {
      toast.error("Orchestrator run failed.");
    } finally {
      setOrchLoading(false);
    }
  };

  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  return (
    <div className="max-w-[1180px] mx-auto space-y-8" data-testid="admin-cockpit">
      {/* Hero */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-3" style={{ color: brandBlue }}>
            Admin Cockpit
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 leading-[1.15]">
            Fleet at a glance.
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl text-[15px] leading-7">
            Live signals across onboarding, warehouse, refresh and recovery — with one-click into each action page.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="cockpit-orchestrator-btn"
            onClick={runOrchestrator}
            disabled={orchLoading}
            className="h-11 px-4 text-white hover:opacity-90"
            style={{ backgroundColor: brandBlue }}
          >
            {orchLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <GitBranch size={14} className="mr-2" />}
            Run Closed-Loop Orchestrator
          </Button>
        </div>
      </div>

      <DashboardView
        region={region}
        onRegionChange={setRegion}
        joiners={joiners}
        orchestration={orchestration}
        onDismissOrch={() => setOrchestration(null)}
        onOpenJoiners={(filter) => { setJoinersFilter(filter || "all"); setJoinersOpen(true); }}
        onOpenDexRisk={(filter) => { setDexFilter(filter || "flagged"); setDexOpen(true); }}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
      />

      <JoinersDrawer
        open={joinersOpen}
        onClose={() => setJoinersOpen(false)}
        joiners={joiners}
        filter={joinersFilter}
        onFilterChange={setJoinersFilter}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
      />

      <DexRiskDrawer
        open={dexOpen}
        onClose={() => setDexOpen(false)}
        cohort={dexCohort}
        filter={dexFilter}
        onFilterChange={setDexFilter}
        brandBlue={brandBlue}
        brandSoft={brandSoft}
      />
    </div>
  );
}

// ==================== DASHBOARD TAB ====================
// Fleet scope bar — sits at the very top of the dashboard and shows that the
// cockpit covers Chevron's global fleet, with region tabs to reframe context.
// KPIs stay global (per product spec); the band is a visible scope indicator.
// Totals are the demo-canonical device-under-management counts.
const REGION_SCOPE = [
  { id: "all",      label: "All regions", devices: 18420, context: "all legal entities" },
  { id: "emea",     label: "EMEA",        devices: 4186,  context: "DE · UK · NL · NO" },
  { id: "americas", label: "Americas",    devices: 10840, context: "US · CA · Houston HQ" },
  { id: "apac",     label: "APAC",        devices: 3394,  context: "SG · AU · PH" },
];

function FleetScopeBar({ region, onRegionChange, brandBlue, brandSoft }) {
  const active = REGION_SCOPE.find((r) => r.id === region) || REGION_SCOPE[0];
  const globalTotal = REGION_SCOPE[0].devices;
  return (
    <div
      className="rounded-2xl bg-white p-4 md:p-5 flex items-center gap-4 flex-wrap"
      style={{ border: `1px solid ${brandSoft}` }}
      data-testid="fleet-scope-bar"
    >
      <div className="flex items-center gap-3 pr-4 border-r border-slate-200">
        <div
          className="w-10 h-10 rounded-xl grid place-items-center"
          style={{ backgroundColor: brandSoft, color: brandBlue }}
        >
          <Globe size={18} />
        </div>
        <div className="leading-tight">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">Global fleet</div>
          <div className="text-[18px] font-bold text-slate-900 tabular-nums">
            {globalTotal.toLocaleString()}
            <span className="text-[11px] text-slate-500 font-semibold ml-1.5">devices under management</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap" data-testid="fleet-scope-tabs">
        {REGION_SCOPE.map((r) => {
          const isActive = r.id === region;
          return (
            <button
              key={r.id}
              onClick={() => onRegionChange(r.id)}
              data-testid={`fleet-scope-tab-${r.id}`}
              className="h-9 px-3 rounded-md text-[11px] font-semibold transition-colors flex items-center gap-1.5"
              style={
                isActive
                  ? { backgroundColor: brandBlue, color: "#fff" }
                  : { color: "#64748B", border: "1px solid #E2E8F0", backgroundColor: "#fff" }
              }
            >
              <span>{r.label}</span>
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums"
                style={{
                  backgroundColor: isActive ? "rgba(255,255,255,0.22)" : "#F1F5F9",
                  color: isActive ? "#fff" : "#64748B",
                }}
              >
                {r.devices.toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ml-auto text-right leading-tight">
        <div className="text-[10px] tracking-[0.14em] uppercase font-bold" style={{ color: brandBlue }}>
          Scope · {active.label}
        </div>
        <div className="text-[11px] text-slate-500" data-testid="fleet-scope-context">
          {active.context}
          <span className="text-slate-300 mx-1.5">·</span>
          {active.devices.toLocaleString()} devices in view
        </div>
      </div>
    </div>
  );
}

// ==================== DASHBOARD TAB ====================
function DashboardView({ region, onRegionChange, joiners, orchestration, onDismissOrch, onOpenJoiners, onOpenDexRisk, brandBlue, brandSoft }) {
  return (
    <div className="space-y-8" data-testid="cockpit-dashboard">
      {/* Fleet scope bar — region tabs rescope every panel below. */}
      <FleetScopeBar region={region} onRegionChange={onRegionChange} brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* Day-1 Onboarding — morning-briefing panel. First thing on the page. */}
      <Day1OnboardingPanel joiners={joiners} onOpenJoiners={onOpenJoiners} brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* Daily-ops KPI row — Fleet · Dark · Warranty · Shelf · Invoices · Refresh · Leavers */}
      <CockpitDailyOpsStrip region={region} brandBlue={brandBlue} brandSoft={brandSoft} onOpenDexRisk={onOpenDexRisk} />

      {/* Simplified Service Requests (REQ only) — compact list + summary strip */}
      <CockpitOpenTickets region={region} brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* Delivery timeline — Q3 · Q4 · Dec live */}
      <CockpitPeriodsTimeline region={region} brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* Orchestrator result strip — appears after Run Closed-Loop Orchestrator */}
      {orchestration && (
        <div
          className="rounded-2xl p-5 md:p-6 relative bg-white"
          style={{ border: `1px solid ${brandSoft}`, animation: "orchIn 300ms ease-out both" }}
          data-testid="orch-result-strip"
        >
          <button
            onClick={onDismissOrch}
            className="absolute top-3 right-3 w-7 h-7 rounded-md grid place-items-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            data-testid="orch-dismiss-btn"
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <GitBranch size={14} style={{ color: brandBlue }} />
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold" style={{ color: brandBlue }}>
              Closed-loop recommendation · {orchestration.summary.confidence} confidence
            </div>
          </div>
          <p className="text-[14px] leading-6 text-slate-800 mb-5 max-w-4xl">{orchestration.summary.recommendation}</p>

          {/* Per-agent outputs — each hop, labelled with the agent that produced it */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            {(orchestration.hops || []).map((h) => (
              <AgentOutputCard key={h.stage} hop={h} brandBlue={brandBlue} brandSoft={brandSoft} />
            ))}
          </div>

          {/* Roll-up totals */}
          <div className="rounded-xl p-4" style={{ backgroundColor: brandSoft }}>
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold mb-3" style={{ color: brandBlue }}>
              Roll-up · net decision impact
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MiniStat label="Net buy"             value={`${orchestration.summary.net_buy_units.toLocaleString()} units`} brandBlue={brandBlue} />
              <MiniStat label="Avoided cost"        value={`$${(orchestration.summary.avoided_cost_usd/1000).toFixed(0)}K`} brandBlue={brandBlue} />
              <MiniStat label="Residual recovered"  value={`$${(orchestration.summary.residual_value_recovered_usd/1000).toFixed(0)}K`} brandBlue={brandBlue} />
              <MiniStat label="Net impact"          value={`$${(orchestration.summary.net_financial_impact_usd/1000).toFixed(0)}K`} brandBlue={brandBlue} />
            </div>
          </div>
          <style>{`@keyframes orchIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
      )}
    </div>
  );
}

// ---------- Day-1 Onboarding Panel ----------
// Morning-briefing card sitting at the top of the cockpit. Groups upcoming
// joiners by start date (today / tomorrow / this week / later) and renders
// each with a device-readiness pill. Clicking a row (or the Open-all CTA)
// opens the existing Joiners drawer.
function Day1OnboardingPanel({ joiners, onOpenJoiners, brandBlue, brandSoft }) {
  const groups = useMemo(() => {
    if (!joiners?.list) return [];
    // Use the demo-canonical anchor date so "today" is Dec 7, 2026.
    const anchor = new Date("2026-12-07T00:00:00Z");
    const toDays = (iso) => {
      if (!iso) return 999;
      const d = new Date(iso + "T00:00:00Z");
      return Math.round((d.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24));
    };
    const buckets = { today: [], tomorrow: [], this_week: [], later: [] };
    for (const j of joiners.list) {
      const dd = toDays(j.start_date);
      if (dd <= 0) buckets.today.push(j);
      else if (dd === 1) buckets.tomorrow.push(j);
      else if (dd <= 7) buckets.this_week.push(j);
      else buckets.later.push(j);
    }
    return [
      { id: "today",     label: "Starting today",     list: buckets.today },
      { id: "tomorrow",  label: "Starting tomorrow",  list: buckets.tomorrow },
      { id: "this_week", label: "Rest of this week",  list: buckets.this_week },
      { id: "later",     label: "Later",              list: buckets.later },
    ].filter((g) => g.list.length > 0);
  }, [joiners]);

  const total = joiners?.total ?? 0;
  const ready = joiners?.day1_ready ?? 0;
  const atRisk = joiners?.at_risk ?? 0;
  const readyPct = joiners?.day1_ready_pct ?? 0;

  if (!joiners) {
    return (
      <div className="rounded-2xl bg-white p-6 flex items-center gap-2 text-slate-500 text-sm" style={{ border: `1px solid ${brandSoft}` }}>
        <Loader2 size={14} className="animate-spin" /> loading day-1 onboarding…
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-white p-5 md:p-6"
      style={{ border: `1px solid ${brandSoft}` }}
      data-testid="day1-onboarding-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
            style={{ backgroundColor: brandSoft, color: brandBlue }}
          >
            <CalendarClock size={20} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold" style={{ color: brandBlue }}>
              Day-1 onboarding
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {total} upcoming joiners · <span style={{ color: "#059669" }}>{ready} ready</span> · <span style={{ color: atRisk > 0 ? "#B45309" : "#64748B" }}>{atRisk} need action</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => onOpenJoiners("all")}
          className="h-9 px-3 rounded-md text-[12px] font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: brandBlue }}
          data-testid="day1-open-all-btn"
        >
          Open joiners pipeline <ArrowRight size={11} className="inline ml-1" />
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        {groups.length === 0 ? (
          <div className="flex-1 text-sm text-slate-500 py-3">No upcoming joiners in the current window.</div>
        ) : (
          <div className="flex-1 min-w-0 flex flex-col md:flex-row gap-3" data-testid="day1-groups">
            {groups.map((g) => (
              <Day1Group key={g.id} group={g} onOpenJoiners={onOpenJoiners} brandBlue={brandBlue} brandSoft={brandSoft} />
            ))}
          </div>
        )}
        <Day1ReadinessSide joiners={joiners} readyPct={readyPct} brandBlue={brandBlue} brandSoft={brandSoft} onOpenJoiners={onOpenJoiners} />
      </div>
    </div>
  );
}

function Day1Group({ group: g, onOpenJoiners, brandBlue, brandSoft }) {
  return (
    <div className="flex-1 min-w-0 rounded-xl p-3" style={{ border: `1px solid ${brandSoft}`, backgroundColor: "#F8FBFF" }} data-testid={`day1-group-${g.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: brandBlue }}>{g.label}</div>
        <div className="text-[11px] font-semibold text-slate-500 tabular-nums">{g.list.length}</div>
      </div>
      <div className="space-y-1.5">
        {g.list.slice(0, 4).map((j) => (
          <button
            key={j.id}
            onClick={() => onOpenJoiners(j.day1_status === "Ready" ? "all" : "at_risk")}
            className="w-full flex items-center gap-2 text-left py-1 rounded hover:bg-white/70 transition-colors"
            data-testid={`day1-joiner-${j.id}`}
          >
            <div className="relative w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold shrink-0" style={{ backgroundColor: brandSoft, color: brandBlue }}>
              {j.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: STATUS_PILL[j.day1_status].color }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-slate-900 truncate">{j.name}</div>
              <div className="text-[10.5px] text-slate-500 truncate" title={`${STATUS_PILL[j.day1_status].label} · ${j.persona || "—"} · ${j.country}`}>
                <span className="font-semibold" style={{ color: STATUS_PILL[j.day1_status].color }} data-testid={`day1-status-${j.id}`}>{STATUS_PILL[j.day1_status].label}</span>
                {" · "}{j.persona || "—"} · {j.country}
              </div>
            </div>
          </button>
        ))}
        {g.list.length > 4 && (
          <button onClick={() => onOpenJoiners("all")} className="text-[11px] font-semibold hover:underline" style={{ color: brandBlue }} data-testid={`day1-more-${g.id}`}>
            +{g.list.length - 4} more
          </button>
        )}
      </div>
    </div>
  );
}

const STATUS_PILL = {
  Ready:     { label: "Shipped", color: "#059669" },
  "At risk": { label: "Pending", color: "#D97706" },
  Blocked:   { label: "Blocked", color: "#DC2626" },
};

// Right-hand readiness summary — ring, status split and the next joiner up.
function Day1ReadinessSide({ joiners, readyPct, brandBlue, brandSoft, onOpenJoiners }) {
  const list = joiners?.list || [];
  const counts = Object.keys(STATUS_PILL).map((k) => ({ k, n: list.filter((j) => j.day1_status === k).length }));
  const next = list.find((j) => j.day1_status !== "Ready");
  const R = 30, C = 2 * Math.PI * R;
  return (
    <div className="lg:w-[260px] shrink-0 rounded-xl p-4 flex flex-col gap-3" style={{ border: `1px solid ${brandSoft}` }} data-testid="day1-readiness-side">
      <div className="flex items-center gap-3">
        <div className="relative w-[76px] h-[76px] shrink-0">
          <svg width="76" height="76" viewBox="0 0 76 76">
            <circle cx="38" cy="38" r={R} fill="none" stroke="#F1F5F9" strokeWidth="8" />
            <circle cx="38" cy="38" r={R} fill="none" stroke="#059669" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${(readyPct / 100) * C} ${C}`} transform="rotate(-90 38 38)" style={{ transition: "stroke-dasharray 500ms ease" }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center text-[15px] font-bold text-slate-900 tabular-nums" data-testid="day1-readiness-pct">{`${readyPct}%`}</div>
        </div>
        <div className="space-y-1 flex-1">
          {counts.map(({ k, n }) => (
            <div key={k} className="flex items-center gap-2 text-[11.5px]" data-testid={`day1-count-${STATUS_PILL[k].label.toLowerCase()}`}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_PILL[k].color }} />
              <span className="text-slate-600 flex-1">{STATUS_PILL[k].label}</span>
              <span className="font-bold text-slate-900 tabular-nums">{n}</span>
            </div>
          ))}
        </div>
      </div>
      {next && (
        <button onClick={() => onOpenJoiners("at_risk")} className="text-left rounded-lg p-2.5 bg-amber-50/60 hover:bg-amber-50 transition-colors" data-testid="day1-next-action">
          <div className="text-[9.5px] font-bold uppercase tracking-wider text-amber-700 mb-0.5">Next to unblock</div>
          <div className="text-[12px] font-semibold text-slate-900 truncate">{next.name} · {next.country}</div>
          <div className="text-[10.5px] text-slate-500 line-clamp-2">{next.blockers?.[0]}</div>
        </button>
      )}
    </div>
  );
}

// ---------- MiniStat ----------


function MiniStat({ label, value, brandBlue }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</div>
      <div className="text-lg font-semibold" style={{ color: brandBlue }}>{value}</div>
    </div>
  );
}

// ---------- Per-agent output card in the orchestrator strip ----------
// Renders one card per orchestrator hop so each output is clearly attributed
// to the agent that produced it (e.g. "Refresh Decision → Devices reused: 117").
function AgentOutputCard({ hop, brandBlue, brandSoft }) {
  // Curated metric labels — keep them presenter-friendly. Anything falling
  // outside this map is skipped rather than shown with a raw snake_case key.
  const METRIC_LABELS = {
    net_buy:          "Net buy (units)",
    forecast:         "Forecast",
    deferrals:        "Deferrals",
    returns:          "Returns reused",
    refresh:          "Devices refreshed",
    extend:           "Devices extended (reused)",
    avoided_cost_usd: "Avoided cost",
    fleet_risk:       "Fleet risk",
    from_returns:     "Reused from returns",
    from_transfer:    "Reused from transfer",
    oem_buy:          "OEM buy",
    vendor:           "Vendor",
    eta_days:         "ETA",
  };
  const fmt = (k, v) => {
    if (typeof v !== "number") return String(v);
    if (k.endsWith("_usd")) return `$${(v / 1000).toFixed(0)}K`;
    if (k === "eta_days") return `${v} days`;
    return v.toLocaleString();
  };
  const rows = Object.entries(hop.output || {})
    .filter(([k]) => METRIC_LABELS[k])
    .map(([k, v]) => ({ key: k, label: METRIC_LABELS[k], value: fmt(k, v), highlight: Math.abs(typeof v === "number" ? v : 0) > 0 && (k === "extend" || k === "from_returns" || k === "from_transfer" || k === "avoided_cost_usd") }));

  return (
    <div
      className="rounded-xl bg-white p-4"
      style={{ border: `1px solid ${brandSoft}` }}
      data-testid={`orch-agent-${hop.stage}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] tracking-[0.14em] uppercase font-bold" style={{ color: brandBlue }}>
          {hop.agent}
        </div>
        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: brandSoft, color: brandBlue }}>
          {hop.stage}
        </span>
      </div>
      <div className="text-[11px] text-slate-500 mb-3 leading-4">{hop.note}</div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.key} className="flex items-baseline justify-between gap-3 text-[12px]">
            <div className="text-slate-600 truncate">{r.label}</div>
            <div className="font-semibold shrink-0" style={{ color: r.highlight ? brandBlue : "#0F172A" }}>
              {r.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Joiners drawer ----------
// Right-side sheet listing every joiner in the pipeline with a filter chip
// (All / At risk). Opened from the Day-1 panel
// and the Joiners Pipeline tile — replaces the old stale link that pointed
// at a retired tabbed page.
//
// Non-Ready rows expand on click to reveal:
//   • full blocker list
//   • suggested fix
//   • Create resolution ticket CTA → opens an INC (mock-fallback aware)
function JoinersDrawer({ open, onClose, joiners, filter, onFilterChange, brandBlue, brandSoft }) {
  const [expandedId, setExpandedId] = useState(null);
  const [tickets, setTickets] = useState({}); // joiner id -> {sn_number, provider}
  const [loadingId, setLoadingId] = useState(null);
  if (!open) return null;
  const list = joiners?.list || [];
  const filtered = filter === "at_risk"
    ? list.filter((j) => j.day1_status === "At risk" || j.day1_status === "Blocked")
    : list;

  const statusPill = (s) => {
    if (s === "Ready")   return "bg-emerald-50 text-emerald-700";
    if (s === "At risk") return "bg-amber-50 text-amber-700";
    return "bg-red-50 text-red-700";
  };

  const createTicket = async (j) => {
    setLoadingId(j.id);
    try {
      const res = await endpoints.lifecycleResolveJoinerBlocker({
        joiner_id: j.id,
        joiner_name: j.name,
        blockers: j.blockers || [],
        suggested_fix: j.suggested_fix,
      });
      setTickets((t) => ({ ...t, [j.id]: res }));
      toast.success(res.message || "Resolution ticket opened");
    } catch (e) {
      toast.error("Could not open resolution ticket");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="joiners-drawer">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        data-testid="joiners-drawer-backdrop"
      />
      <div
        className="relative ml-auto h-full w-full max-w-[520px] bg-white shadow-xl flex flex-col"
        style={{ animation: "slideInRight 220ms ease-out both" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold" style={{ color: brandBlue }}>
              Joiners Pipeline
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {joiners ? `${joiners.total} joiners · ${joiners.day1_ready} ready · ${joiners.at_risk} at risk` : "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md grid place-items-center text-slate-500 hover:bg-slate-100"
            data-testid="joiners-drawer-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex items-center gap-1 px-6 py-3 border-b border-slate-100">
          {[
            { id: "all",     label: `All (${list.length})` },
            { id: "at_risk", label: `At risk (${list.filter((j) => j.day1_status !== "Ready").length})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => onFilterChange(f.id)}
              className="text-xs font-semibold h-8 px-3 rounded-md transition-colors"
              style={
                filter === f.id
                  ? { backgroundColor: brandBlue, color: "#fff" }
                  : { color: "#64748B", border: "1px solid #E2E8F0" }
              }
              data-testid={`joiners-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-12">No joiners match this filter.</div>
          ) : filtered.map((j) => {
            const isNonReady = j.day1_status !== "Ready";
            const expanded = expandedId === j.id;
            const ticket = tickets[j.id];
            return (
              <div
                key={j.id}
                className="rounded-lg border border-slate-200 overflow-hidden bg-white"
                data-testid={`joiner-row-${j.id}`}
              >
                <div
                  className={`p-3 flex items-start gap-3 ${isNonReady ? "cursor-pointer hover:bg-slate-50" : ""}`}
                  onClick={() => isNonReady && setExpandedId(expanded ? null : j.id)}
                >
                  <div className="w-9 h-9 rounded-full grid place-items-center text-[11px] font-bold shrink-0" style={{ backgroundColor: brandSoft, color: brandBlue }}>
                    {j.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-slate-900 truncate">{j.name}</div>
                      <div className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${statusPill(j.day1_status)}`}>
                        {j.day1_status}
                      </div>
                      {ticket && (
                        <span
                          className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
                          data-testid={`joiner-ticket-${j.id}`}
                        >
                          {ticket.sn_number}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {j.country} · {j.persona || "—"} · start {j.start_date || "—"}
                    </div>
                    {isNonReady && j.blockers && j.blockers.length > 0 && (
                      <div className="text-[11px] text-red-700 mt-1.5">
                        <span className="font-semibold">Blockers:</span> {j.blockers.join(" · ")}
                      </div>
                    )}
                  </div>
                </div>

                {expanded && isNonReady && (
                  <div
                    className="px-4 py-3 border-t"
                    style={{ backgroundColor: brandSoft, borderColor: `${brandBlue}22` }}
                    data-testid={`joiner-resolve-${j.id}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: brandBlue }}>
                      Suggested fix
                    </div>
                    <div className="text-[12.5px] text-slate-800 mb-3 leading-5">
                      {j.suggested_fix || "Review with onboarding desk."}
                    </div>
                    {ticket ? (
                      <div
                        className="rounded-md px-3 py-2 flex items-center gap-2 text-[11.5px] font-semibold bg-emerald-50 text-emerald-700"
                      >
                        <CheckCircle2 size={13} />
                        {ticket.message}
                      </div>
                    ) : (
                      <Button
                        data-testid={`joiner-resolve-btn-${j.id}`}
                        onClick={() => createTicket(j)}
                        disabled={loadingId === j.id}
                        className="h-8 text-xs text-white hover:opacity-90"
                        style={{ backgroundColor: brandBlue }}
                      >
                        {loadingId === j.id ? (
                          <Loader2 size={11} className="mr-1.5 animate-spin" />
                        ) : (
                          <Wrench size={11} className="mr-1.5" />
                        )}
                        Create resolution ticket
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <style>{`
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(20px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>
      </div>
    </div>
  );
}

// ---------- DEX Risk drawer ----------
// Opened from the Fleet health tile. Mirrors the Joiners
// drawer pattern (same width, same filter chip pair, same close affordance)
// so viewers only have to learn one model. Devices rendered via the shared
// <DexDeviceRow> so the Cockpit drawer and the inline cohort panel on
// /lifecycle behave identically.
function DexRiskDrawer({ open, onClose, cohort, filter, onFilterChange, brandBlue, brandSoft }) {
  const [expandedId, setExpandedId] = useState(null);
  if (!open) return null;
  const list = cohort?.list || [];
  const filtered = filter === "flagged" ? list.filter((d) => d.flagged) : list;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="dex-drawer">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        data-testid="dex-drawer-backdrop"
      />
      <div
        className="relative ml-auto h-full w-full max-w-[520px] bg-white shadow-xl flex flex-col"
        style={{ animation: "slideInRight 220ms ease-out both" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase font-bold" style={{ color: brandBlue }}>
              Devices at DEX risk · {cohort?.cohort_label || "—"}
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {cohort ? `${cohort.total} monitored · ${cohort.flagged} flagged · avg DEX ${cohort.avg_dex}` : "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md grid place-items-center text-slate-500 hover:bg-slate-100"
            data-testid="dex-drawer-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex items-center gap-1 px-6 py-3 border-b border-slate-100">
          {[
            { id: "all",     label: `All (${list.length})` },
            { id: "flagged", label: `Flagged (${list.filter((d) => d.flagged).length})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => onFilterChange(f.id)}
              className="text-xs font-semibold h-8 px-3 rounded-md transition-colors"
              style={
                filter === f.id
                  ? { backgroundColor: brandBlue, color: "#fff" }
                  : { color: "#64748B", border: "1px solid #E2E8F0" }
              }
              data-testid={`dex-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {!cohort ? (
            <div className="text-sm text-slate-500 text-center py-12">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-12">No devices match this filter.</div>
          ) : filtered.map((d) => (
            <DexDeviceRow
              key={d.device_id}
              device={d}
              expanded={expandedId === d.device_id}
              onToggle={() => setExpandedId(expandedId === d.device_id ? null : d.device_id)}
              brandBlue={brandBlue}
              brandSoft={brandSoft}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
